import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Select, Tooltip } from 'antd'
import { notification } from '@edulastic/common'
import { IconBlueQuestionCircle } from '@edulastic/icons'
import {
  getUserIdSelector,
  getWritableCollectionsSelector,
  getCollectionsSelector,
  getUserRole,
  getIsCurator,
} from '../../src/selectors/user'
import { getTestItemsSelector } from '../../TestPage/ducks'
import {
  fetchRubricsForDropdown,
  getEligibleRubricApplyTargets,
} from '../utils/rubricApply'
import {
  StyledStandardSetInputContainer,
  StyledFieldLabel,
  StyledStandardSetDropdownContainer,
  StyledStandardSetTooltip,
  StyledStandardTooltipWrapper,
  StyledStandardSetTooltipContent,
  StyledDisabledSelectHoverTarget,
  StyledRubricHelperText,
} from './styled'
import { StyledStandardsSelect } from '../AutoSuggestTestItemsStandards/styled'

const { Option } = Select

const NO_ELIGIBLE_ITEMS_TOOLTIP =
  'No eligible Essay items in this test. Rubric applies only to essays you can edit or clone that are missing standards and do not already have a rubric.'
const RUBRIC_HELPER_TEXT = 'Apply Rubric to all essay item types'
const NO_RUBRIC_AVAILABLE_PLACEHOLDER = 'No Rubric Available'
const NO_RUBRICS_CREATE_TOOLTIP =
  'No rubrics available. Create one while authoring an item to use it here.'

/**
 * Pear Assist Rubric select for test-level Generate Standards drawer (CON-756).
 * Selection only — Continue reads `getSelectedRubricId` via `onRegisterApi`.
 * The generate saga stamps the rubric onto essay metadata suggestions.
 * @see https://www.figma.com/design/D4HCBpO9yAkuipVykLdise/Auto-Suggest-Standards?node-id=4432-892
 */
const PearAssistRubricSection = ({
  isOpen,
  testItems,
  userId,
  writableCollections,
  userCollections,
  userRole,
  isCurator,
  onRegisterApi,
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedRubricId, setSelectedRubricId] = useState(undefined)
  const selectedRubricIdRef = useRef(selectedRubricId)
  selectedRubricIdRef.current = selectedRubricId

  const hasWritableEligibleEssays = useMemo(() => {
    const { targets } = getEligibleRubricApplyTargets({
      testItems,
      userId,
      writableCollections,
      userCollections,
      userRole,
      isCurator,
      requireMissingStandards: true,
    })
    return targets.length > 0
  }, [
    testItems,
    userId,
    writableCollections,
    userCollections,
    userRole,
    isCurator,
  ])

  const selectDisabled = !hasWritableEligibleEssays || loading
  const showNoAvailable =
    hasWritableEligibleEssays && !loading && options.length === 0
  const rubricSelectTooltipTitle = !hasWritableEligibleEssays
    ? NO_ELIGIBLE_ITEMS_TOOLTIP
    : showNoAvailable
    ? NO_RUBRICS_CREATE_TOOLTIP
    : undefined
  const ariaDescribedBy = showNoAvailable
    ? 'pearAssistRubricHelper pearAssistRubricEmptyHint'
    : 'pearAssistRubricHelper'

  useEffect(() => {
    if (!isOpen) {
      setSelectedRubricId(undefined)
      setOptions([])
      return undefined
    }

    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const next = await fetchRubricsForDropdown(userId, {
          throwOnError: true,
        })
        if (!cancelled) {
          setOptions(next)
        }
      } catch (err) {
        if (!cancelled) {
          setOptions([])
          notification({
            type: 'warning',
            msg: 'Unable to load rubrics. Please try again.',
          })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, userId])

  const handleSelect = useCallback(
    (rubricId) => {
      if (selectDisabled) return
      setSelectedRubricId(rubricId || undefined)
    },
    [selectDisabled]
  )

  useEffect(() => {
    if (typeof onRegisterApi !== 'function') return undefined
    onRegisterApi({
      getSelectedRubricId: () => selectedRubricIdRef.current,
    })
    return () => onRegisterApi(null)
  }, [onRegisterApi])

  return (
    <StyledStandardSetDropdownContainer data-cy="pearAssistRubricSection">
      <StyledStandardSetInputContainer>
        <StyledFieldLabel>
          <span id="pearAssistRubricLabel">Rubric</span>
        </StyledFieldLabel>
        <StyledStandardSetTooltip
          title={
            <StyledStandardSetTooltipContent>
              Choose a rubric to apply on all essay items on this test
            </StyledStandardSetTooltipContent>
          }
        >
          <StyledStandardTooltipWrapper>
            <IconBlueQuestionCircle
              width="16px"
              height="16px"
              aria-hidden="true"
            />
          </StyledStandardTooltipWrapper>
        </StyledStandardSetTooltip>
      </StyledStandardSetInputContainer>
      {showNoAvailable && (
        <span className="sr-only" id="pearAssistRubricEmptyHint">
          {NO_RUBRICS_CREATE_TOOLTIP}
        </span>
      )}
      <Tooltip title={rubricSelectTooltipTitle}>
        <StyledDisabledSelectHoverTarget>
          <StyledStandardsSelect
            data-cy="pearAssistRubricSelect"
            aria-label="Rubric"
            aria-labelledby="pearAssistRubricLabel"
            aria-describedby={ariaDescribedBy}
            placeholder={
              showNoAvailable
                ? NO_RUBRIC_AVAILABLE_PLACEHOLDER
                : 'Select rubric'
            }
            value={selectedRubricId}
            onChange={handleSelect}
            disabled={selectDisabled || showNoAvailable}
            loading={loading}
            allowClear
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            {options.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </StyledStandardsSelect>
        </StyledDisabledSelectHoverTarget>
      </Tooltip>
      <StyledRubricHelperText id="pearAssistRubricHelper">
        {RUBRIC_HELPER_TEXT}
      </StyledRubricHelperText>
    </StyledStandardSetDropdownContainer>
  )
}

PearAssistRubricSection.propTypes = {
  isOpen: PropTypes.bool,
  testItems: PropTypes.array,
  userId: PropTypes.string,
  writableCollections: PropTypes.array,
  userCollections: PropTypes.array,
  userRole: PropTypes.string,
  isCurator: PropTypes.bool,
  onRegisterApi: PropTypes.func,
}

PearAssistRubricSection.defaultProps = {
  isOpen: false,
  testItems: [],
  userId: '',
  writableCollections: [],
  userCollections: [],
  userRole: '',
  isCurator: false,
  onRegisterApi: undefined,
}

export default connect((state) => ({
  testItems: getTestItemsSelector(state),
  userId: getUserIdSelector(state),
  writableCollections: getWritableCollectionsSelector(state),
  userCollections: getCollectionsSelector(state),
  userRole: getUserRole(state),
  isCurator: getIsCurator(state),
}))(PearAssistRubricSection)
